import React, {useRef, useEffect, useState, useCallback} from 'react';
import LoadScript from './LoadScript.js';
import ScriptGraphics from './ScriptGraphics.js';
import {USER_SCRIPT} from 'constants/script-types.js';
import MatchingParenthesis from './MatchingParenthesis.js';
import SaveScript from './SaveScript.js';
import SyntaxHighlighter from './SyntaxHighlighter.js';
import Possibilities from './Possibilities.js';
import {usePreventDragging} from 'components/effects/preventDragging.js';
import Colors from 'components/Colors.js';
import {connect} from 'react-redux';
import EffectParameterSlider from '../EffectParameterSlider.js';
import { Input } from 'reactstrap';
import {useSyntaxHelper} from './useSyntaxHelper.js';
import {autoIndent} from './autoindent.js';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/themes/prism-dark.css';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import '../../../scripting.scss';
import '../../../Syntax.scss';
window.startColor = "#ff4d24";
window.hueShift = 4;
const colors = new Colors();

const UserScriptComponent = ({isDraggable, logs, scriptType=USER_SCRIPT, effect, sequencerEngine, variables, doc, setExpanded, expanded}) => {
    const ref = useRef(undefined);
    const input = useRef(undefined);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [code, setCode] = useState(effect.code);
    const caret = useRef(0);
    let shouldIndent = useRef(false);
    const [running, setRunning] = useState(false);
    const [syntaxError, setSyntaxError] = useState(false);
    const [scrollTop, setScrollTop] = useState(0);
    const {preventDragging} = usePreventDragging({ref});

    useEffect(() => {
        setCode(effect.code);
    }, [effect, setCode]);

    useEffect(() => {
        if (effect && effect.expanded && scriptType === USER_SCRIPT) {
            setExpanded(true);
        }
    }, []);

    const onChange = useCallback((e) => {
        let el = input.current;
        setScrollTop(el.scrollTop);
        setCode(e.target.value);
        if (effect.hasSyntaxError(e.target.value)) {
            setSyntaxError(true);
        } else {
            setSyntaxError(false);
        }
    }, [setCode, setSyntaxError, setScrollTop]);

    const {choosePossibility, possibilities, setPossibilities, selectedChoice, matchingParenthesis} =
          useSyntaxHelper({ref: input, code, onChange, doc});


    useEffect(() => {
        if (!shouldIndent.current) {
            return;
        }
        if (!input.current) {
            return;
        }
        shouldIndent.current = false;
        let ret = autoIndent(code, caret.current + 1);
        if (ret.indented === code) {
            return;
        }
        setCode(ret.indented);
        setTimeout(() => {
            input.current.setSelectionRange(ret.caret, ret.caret);
        }, 25);
    }, [code, setCode]);

    useEffect(() => {
        setCode(effect.code);
        let el = input.current;
        if (el) {
            el.addEventListener('scroll', onScroll);
        }
        return unbindScrolling;
    }, [effect, setCode]);

    function unbindScrolling() {
        let el = document.getElementById(effect.id + '-script');
        if (el) {
            el.removeEventListener('scroll', onScroll);
        }
    }

    const onScroll = useCallback(() => {
        let el = document.getElementById(effect.id + '-script');
        if (el) {
            setScrollTop(el.scrollTop);
        }
    }, [setScrollTop]);

    const saveScript = useCallback(() => {
        console.log("save script");
    }, [code]);

    const execute = useCallback(() => {
        if (!effect.eval(code)) {
        }
        setRunning(true);
        setTimeout(() => setRunning(false), 100);
    }, [code, setRunning]);

    const onKeyUp = useCallback((e) => {
        if (input.current) {
            caret.current = input.current.selectionStart;
        }
        if (e.key === 'Enter' && e.ctrlKey) {
            e.stopPropagation();
            e.preventDefault();
            execute();
        } else if (e.key === 'Enter') {
            shouldIndent.current = true;
        }
    }, [execute]);


    let executeClass = "execute-button";
    let containerClass = "user-script-component";
    if (running) {
        executeClass += " running";
        containerClass += " running";
    }

    useEffect(() => {
        input.current = document.getElementById(effect.id + '-script');
    }, [effect]);
    if (expanded){
        containerClass += " expanded";
    }

    let parsedLog = '';
    if (logs) {
        parsedLog = logs[logs.length - 1];
    }
    return (
        <div ref={ref} className={containerClass}>
          {setExpanded ? <img
                           onClick={() => {
                               effect.expanded = expanded ? false : true;
                               setExpanded(expanded ? undefined : effect);
                           }}
                           className="expanded-icon" src="/expand-icon.svg"/> : ''}
          <div
            draggable={true}
            onDragStart={(e) => e.preventDefault()}
            onMouseDown={() => setPossibilities([])}
            className="script-box">
            <SaveScript scriptType={scriptType} setIsSaving={setIsSaving} code={code} isSaving={isSaving}/>
            <LoadScript setCode={setCode} code={code} scriptType={scriptType} setIsLoading={setIsLoading} code={code} isLoading={isLoading}/>
            <Editor
              textareaId={effect.id + '-script'}
              value={code}
              onValueChange={code => onChange({target: {value: code}})}
              highlight={code => highlight(code, languages.js)}
              onKeyDown={onKeyUp}
              onMouseDown={(e) => {
                  e.stopPropagation();
              }}
              spellcheck="false"
              className="user-script-code"/>
            <Highlighter scrollTop={scrollTop} running={running} syntaxError={syntaxError} code={code}/>
            <MatchingParenthesis matchingParenthesis={matchingParenthesis}/>
            <Possibilities
              doc={doc}
              selectedChoice={selectedChoice}
              choosePossibility={choosePossibility}
              scrollTop={scrollTop}
              input={input}
              setPossibilities={setPossibilities}
              possibilities={possibilities}/>
          </div>
          {!isLoading && !isSaving ? 
           <div className="script-toolbar">
             <div
               className={"load-script-btn"}
               onClick={() => setIsLoading(true)}
             >
               Load
             </div>
             <div
               className={"save-script-btn"}
               onClick={() => setIsSaving(true)}
             >
               Save
             </div>
             <div
               className={executeClass}
               onClick={execute}
             >
               <div className="arrow-right"></div>
               <span>
                 Run
               </span>
             </div>
           </div>
           : ''}
          <ScriptGraphics effect={effect}/>
          {(variables || []).map(
                variable =>
                    <EffectParameterSlider
                      name={variable.name}
                      min={variable.min}
                      max={variable.max}
                      step={variable.max - Math.floor(variable.max) !== 0 ||
                             variable.min - Math.floor(variable.min) !== 0 ||
                             Math.abs(variable.max - variable.min) < 2 ? 0.1 : 1}
                      parameter={variable}
                      lfoEnabled={true}
                      sequencerEngine={sequencerEngine}
                      isParameter={true}
                      effectNumber={0}
                    />)}
          <div className="log-display">
            {parsedLog}
          </div>
        </div>
    );
};



const Highlighter = ({code, running, syntaxError, scrollTop}) => {
    let splitted = (code || "").split("\n");
    const height = 19;
    const CHAR_WIDTH = 8;
    let className = "highlighter";
    if (syntaxError) {
        className += " syntax-error";
    }
    if (running) {
        className += " running";
    }
    let color = colors.saturate(window.startColor, 1);
    return splitted.map(
        (line, i) => (
            <div
              key={i}
              style={{
                  //backgroundColor: colors.hueShift(color, i*window.hueShift),
                  width: 20 + line.length * CHAR_WIDTH, top: -2 + height * i - scrollTop, height: height}}
              className={className}>
            </div>));
};

const mapStateToProps = (state, props) => {
    return {
        variables: state.effects.scriptVariables[props.effect.id],
        logs: props.effect.logs,
        logNum: props.effect.logs && props.effect.logs.length,
    };
};

export default connect(mapStateToProps, null)(UserScriptComponent);
